import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcCheck    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcZap      = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
const IcCard     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
const IcAlert    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
const IcDownload = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
const IcRefresh  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
const IcExternal = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
const IcX        = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const IcUpload   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
const IcCrown    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/><line x1="2" y1="20" x2="22" y2="20"/></svg>

// ── Usage bar (small) ─────────────────────────────────────────────────────────
function UsageBar({ label, used, total, unit = '' }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0
  const cls = pct >= 90 ? 'crit' : pct >= 70 ? 'warn' : ''
  return (
    <div>
      <div className="usage-bar-label">
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace' }}>
          {typeof used === 'number' ? used : '?'}{unit} / {typeof total === 'number' ? total : '?'}{unit}
        </span>
      </div>
      <div className="usage-track">
        <div className={`usage-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ── Invoice status badge ──────────────────────────────────────────────────────
function InvStatus({ s }) {
  const cls = s === 'paid' ? 'invoice-status-paid' : s === 'pending' ? 'invoice-status-pending' : 'invoice-status-failed'
  return <span className={cls}>{s}</span>
}

// ── Plan card ─────────────────────────────────────────────────────────────────
function PlanCard({ planId, plan, currentPlan, isCancelled, onUpgrade, loading }) {
  const isCurrent = currentPlan === planId
  const feats = plan.features || {}
  return (
    <div className={`plan-card${isCurrent ? ' current' : ''}`}>
      {isCurrent && <span className="plan-current-badge">CURRENT</span>}
      <div className="plan-name">{plan.name}</div>
      <div style={{ marginTop: 8 }}>
        <span className="plan-price">£{plan.price}</span>
        <span className="plan-period">/month</span>
      </div>
      <ul className="plan-features">
        <li className="plan-feature"><IcCheck /> {feats.sites === 'unlimited' ? 'Unlimited' : feats.sites || 1} website{feats.sites !== 1 ? 's' : ''}</li>
        <li className="plan-feature"><IcCheck /> {feats.editsPerMonth === 'unlimited' ? 'Unlimited' : feats.editsPerMonth || 10} edits/month</li>
        <li className="plan-feature"><IcCheck /> {feats.storage || '1GB'} storage</li>
        {feats.support && <li className="plan-feature"><IcCheck /> {feats.support}</li>}
      </ul>
      {isCurrent && !isCancelled ? (
        <button className="ds-btn ds-btn-ghost" style={{ width: '100%', justifyContent: 'center' }} disabled>Current plan</button>
      ) : isCurrent && isCancelled ? (
        <button className="ds-btn ds-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => onUpgrade(planId)} disabled={loading}>
          {loading ? <IcRefresh /> : <IcRefresh />} Renew
        </button>
      ) : (
        <button className="ds-btn ds-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => onUpgrade(planId)} disabled={loading}>
          {loading ? 'Processing…' : plan.price > 50 ? <><IcCrown /> Upgrade</> : 'Switch plan'}
        </button>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const ClientBilling = () => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showCancelModal, setShowCancelModal]   = useState(false)
  const [selectedPlan, setSelectedPlan]         = useState(null)
  const queryClient = useQueryClient()

  // ── Preserved API calls ───────────────────────────────────────────────────
  const { data: billing, isLoading: billingLoading } = useQuery(
    'client-billing',
    () => apiService.get('/billing').then(res => res.data)
  )

  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data)
  )

  const { data: plans, isLoading: plansLoading } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  const { data: stripeConfig } = useQuery(
    'stripe-config',
    () => apiService.get('/billing/config').then(res => res.data)
  )

  const createCheckoutMutation = useMutation(
    async (planId) => {
      const response = await apiService.post('/billing/create-checkout-session', { planId })
      return response.data
    },
    {
      onSuccess: (data) => { window.location.href = data.checkoutUrl },
      onError: (err) => {
        console.error('Checkout error:', err)
        toast.error('Failed to create checkout session. Please try again.')
      }
    }
  )

  const cancelSubscriptionMutation = useMutation(
    () => apiService.post('/billing/cancel'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-billing')
        queryClient.invalidateQueries('client-profile')
        toast.success('Subscription cancelled successfully!')
        setShowCancelModal(false)
      },
      onError: (err) => {
        console.error('Cancel error:', err)
        toast.error('Failed to cancel subscription. Please try again.')
      }
    }
  )

  const handleUpgrade = (planId) => {
    setSelectedPlan({ id: planId, ...plans?.[planId] })
    setShowUpgradeModal(true)
  }

  const confirmUpgrade = () => {
    createCheckoutMutation.mutate(selectedPlan.id)
    setShowUpgradeModal(false)
  }

  if (billingLoading || profileLoading || plansLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading billing information…</div>
  }

  const currentPlan = profile?.subscription_plan || 'basic'
  const planData    = plans?.[currentPlan]
  const isFreePlan  = planData?.price === 0
  const isActive    = profile?.billing_status === 'active' || isFreePlan
  const isCancelled = profile?.billing_status === 'canceled' || profile?.billing_status === 'cancelled'

  // Mock invoices (billing API may return them)
  const invoices = billing?.invoices || []

  const editsUsed   = profile?.edits_used    || 0
  const editQuota   = profile?.edit_quota    || (planData?.features?.editsPerMonth === 'unlimited' ? 999 : planData?.features?.editsPerMonth || 50)
  const storageMb   = profile?.storage_used_mb || 0
  const siteCount   = profile?.site_count    || 0
  const sitesLimit  = planData?.features?.sites === 'unlimited' ? 999 : planData?.features?.sites || 3

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 40 }}>

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'var(--text)' }}>Billing</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Your plan, payment method and invoices.</div>
        </div>
        <button className="ds-btn ds-btn-ghost"><IcUpload /> Download invoices</button>
      </div>

      {/* Plan card + Credit card visual */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14, alignItems: 'stretch' }}>

        {/* Plan card */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(108,99,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                <IcZap />
              </span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{planData?.name || 'Basic Plan'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Billed monthly</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', fontFamily: 'monospace', letterSpacing: '-0.02em' }}>
                £{planData?.price ?? '0'}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)', fontFamily: 'inherit' }}>/mo</span>
              </div>
              {profile?.quota_reset_date && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Next charge {new Date(profile.quota_reset_date).toLocaleDateString()}</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <UsageBar label="Edits used" used={editsUsed} total={planData?.features?.editsPerMonth === 'unlimited' ? editsUsed + 100 : editQuota} />
            <UsageBar label="Sites" used={siteCount} total={sitesLimit} />
            <UsageBar label="Storage" used={storageMb} total={1024} unit=" MB" />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            {plans && Object.entries(plans).some(([id]) => id !== currentPlan) && (
              <button className="ds-btn ds-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                <IcZap /> Upgrade plan
              </button>
            )}
            {isActive && !isFreePlan && (
              <button className="ds-btn ds-btn-danger" onClick={() => setShowCancelModal(true)}>
                <IcAlert /> Cancel
              </button>
            )}
          </div>
        </div>

        {/* Credit card visual + payment summary */}
        <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Payment method</h2>
          <div className="credit-card-visual">
            <div className="credit-card-shine" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <IcCard />
              <span className="credit-card-brand">VISA</span>
            </div>
            <div className="credit-card-number">•••• •••• •••• 4242</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span className="credit-card-name">{profile?.company_name?.toUpperCase() || 'CARDHOLDER'}</span>
              <span className="credit-card-expiry">09 / 28</span>
            </div>
          </div>
          <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}>
            Update payment method
          </button>

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Status',       value: isActive ? 'Active' : (isCancelled ? 'Cancelled' : 'Inactive') },
              { label: 'Plan',         value: planData?.name || currentPlan },
              { label: 'Next invoice', value: profile?.quota_reset_date ? `£${planData?.price || 0} · ${new Date(profile.quota_reset_date).toLocaleDateString()}` : 'N/A' },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                <span style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Plan comparison grid */}
      <div>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Compare plans</h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Switch any time — changes are prorated.</div>
        {plans && Object.keys(plans).length > 0 ? (
          <div className="plan-grid">
            {Object.entries(plans).map(([planId, plan]) => (
              <PlanCard
                key={planId}
                planId={planId}
                plan={plan}
                currentPlan={currentPlan}
                isCancelled={isCancelled}
                onUpgrade={handleUpgrade}
                loading={createCheckoutMutation.isLoading}
              />
            ))}
          </div>
        ) : (
          /* Fallback plan grid when API returns nothing */
          <div className="plan-grid">
            {[
              { id: 'basic',    name: 'Lite',   price: 19, feats: ['1 website', '5 edits/month', '1GB storage', 'Email support'] },
              { id: 'pro',      name: 'Growth', price: 49, feats: ['3 websites', '20 edits/month', '2GB storage', 'Priority support'] },
              { id: 'business', name: 'Studio', price: 89, feats: ['10 websites', 'Unlimited edits', '10GB storage', 'Dedicated manager'] },
            ].map(plan => (
              <div key={plan.id} className={`plan-card${currentPlan === plan.id ? ' current' : ''}`}>
                {currentPlan === plan.id && <span className="plan-current-badge">CURRENT</span>}
                <div className="plan-name">{plan.name}</div>
                <div style={{ marginTop: 8 }}>
                  <span className="plan-price">£{plan.price}</span>
                  <span className="plan-period">/month</span>
                </div>
                <ul className="plan-features">
                  {plan.feats.map(f => <li key={f} className="plan-feature"><IcCheck /> {f}</li>)}
                </ul>
                <button className={`ds-btn ds-btn-sm${currentPlan === plan.id ? ' ds-btn-ghost' : ' ds-btn-primary'}`} style={{ width: '100%', justifyContent: 'center' }} disabled={currentPlan === plan.id}>
                  {currentPlan === plan.id ? 'Your plan' : plan.price > 49 ? 'Upgrade' : 'Switch'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoice table */}
      <div className="card card-pad">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Invoice history</h2>
          {invoices.length > 0 && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,.12)', padding: '2px 8px', borderRadius: 20 }}>
              All paid
            </span>
          )}
        </div>
        {invoices.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
            No invoices yet
          </div>
        ) : (
          <div className="invoice-table">
            <div className="invoice-row header">
              <div>Invoice</div><div>Description</div><div>Amount</div><div>Status</div><div>Date</div><div></div>
            </div>
            {invoices.map((inv, i) => (
              <div key={i} className="invoice-row">
                <div className="invoice-num">{inv.id || inv.invoice_id || `INV-${i + 1}`}</div>
                <div className="invoice-desc">{inv.description || inv.desc || `${planData?.name || 'Plan'} · monthly`}</div>
                <div className="invoice-amt">£{inv.amount || inv.total || '0'}</div>
                <div><InvStatus s={inv.status || 'paid'} /></div>
                <div style={{ fontFamily: 'monospace', fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {inv.date || inv.created_at ? new Date(inv.date || inv.created_at).toLocaleDateString() : '—'}
                </div>
                <div>
                  {inv.download_url && (
                    <a href={inv.download_url} target="_blank" rel="noopener noreferrer">
                      <button className="invoice-dl"><IcDownload /></button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade modal */}
      {showUpgradeModal && selectedPlan && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={() => setShowUpgradeModal(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 420, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                {isCancelled && currentPlan === selectedPlan.id ? 'Renew subscription' : currentPlan === 'basic' ? 'Upgrade plan' : 'Switch plan'}
              </h3>
              <button className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => setShowUpgradeModal(false)}><IcX /></button>
            </div>
            <div style={{ padding: 16, borderRadius: 12, border: '1px solid var(--accent)', background: 'rgba(108,99,255,.06)', marginBottom: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{selectedPlan.name}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--accent)', fontFamily: 'monospace', marginTop: 4 }}>£{selectedPlan.price}/month</div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16, padding: 12, background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)' }}>
              You'll be redirected to Stripe to complete your subscription. Billing will be adjusted automatically.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ds-btn ds-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowUpgradeModal(false)}>Cancel</button>
              <button className="ds-btn ds-btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={confirmUpgrade} disabled={createCheckoutMutation.isLoading}>
                {createCheckoutMutation.isLoading ? <><IcRefresh /> Processing…</> : <><IcExternal /> Continue to Stripe</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={() => setShowCancelModal(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 400, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                <IcAlert />
              </span>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#ef4444', margin: 0 }}>Cancel subscription</h3>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
              Are you sure? Your subscription will remain active until the end of the billing period, then your account will revert to limited access.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ds-btn ds-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowCancelModal(false)}>Keep subscription</button>
              <button
                className="ds-btn"
                style={{ flex: 1, justifyContent: 'center', background: '#ef4444', color: 'white', borderColor: '#ef4444' }}
                onClick={() => cancelSubscriptionMutation.mutate()}
                disabled={cancelSubscriptionMutation.isLoading}
              >
                {cancelSubscriptionMutation.isLoading ? 'Cancelling…' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientBilling
